// ===========================================
// Newsletter Editor Page
// ===========================================
// Admin interface for creating and editing newsletters

import { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Visibility as PreviewIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useNewsletter,
  useCreateNewsletter,
  useUpdateNewsletter,
  useAddSection,
  useUpdateSection,
  useDeleteSection,
  useReorderSections,
  usePublishNewsletter,
  useSendNewsletter,
  useScheduleNewsletter,
} from '../../hooks/useNewsletter';
import {
  NewsletterSection,
  ContentVisibility,
  SectionType,
  CreateNewsletterData,
  UpdateNewsletterData,
  CreateSectionData,
  UpdateSectionData,
} from '../../types/newsletter.types';

// ===========================================
// SECTION EDITOR COMPONENT
// ===========================================

interface SectionEditorProps {
  section: NewsletterSection;
  onUpdate: (data: UpdateSectionData) => void;
  onDelete: () => void;
  isUpdating: boolean;
}

function SectionEditor({ section, onUpdate, onDelete, isUpdating }: SectionEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(section.title || '');
  const [content, setContent] = useState(section.content);
  const [sectionType, setSectionType] = useState<SectionType>(section.sectionType);

  const handleSave = () => {
    onUpdate({
      title: title || null,
      content,
      sectionType,
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Section Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={sectionType}
                label="Type"
                onChange={(e) => setSectionType(e.target.value as SectionType)}
              >
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
                <MenuItem value="EVENT">Event</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={6}
            fullWidth
            placeholder="Enter section content (HTML supported)"
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setIsEditing(false)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isUpdating || !content.trim()}
            >
              {isUpdating ? <CircularProgress size={20} /> : 'Save Section'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ cursor: 'grab', mt: 1 }}>
          <DragIcon color="disabled" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Chip label={sectionType} size="small" variant="outlined" />
            {title && (
              <Typography variant="subtitle2" fontWeight={600}>
                {title}
              </Typography>
            )}
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          />
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => setIsEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ===========================================
// ADD SECTION DIALOG
// ===========================================

interface AddSectionDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: CreateSectionData) => void;
  isAdding: boolean;
}

function AddSectionDialog({ open, onClose, onAdd, isAdding }: AddSectionDialogProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [sectionType, setSectionType] = useState<SectionType>('TEXT');

  const handleAdd = () => {
    onAdd({
      title: title || undefined,
      content,
      sectionType,
    });
    setTitle('');
    setContent('');
    setSectionType('TEXT');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Section</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Section Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={sectionType}
                label="Type"
                onChange={(e) => setSectionType(e.target.value as SectionType)}
              >
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
                <MenuItem value="EVENT">Event</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={8}
            fullWidth
            placeholder="Enter section content (HTML supported)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isAdding}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={isAdding || !content.trim()}
        >
          {isAdding ? <CircularProgress size={20} /> : 'Add Section'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// SCHEDULE DIALOG
// ===========================================

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (date: string) => void;
  isScheduling: boolean;
}

function ScheduleDialog({ open, onClose, onSchedule, isScheduling }: ScheduleDialogProps) {
  const [scheduledFor, setScheduledFor] = useState('');

  const handleSchedule = () => {
    if (scheduledFor) {
      onSchedule(new Date(scheduledFor).toISOString());
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Schedule Newsletter</DialogTitle>
      <DialogContent>
        <TextField
          label="Schedule For"
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().slice(0, 16) }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isScheduling}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSchedule}
          disabled={isScheduling || !scheduledFor}
        >
          {isScheduling ? <CircularProgress size={20} /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function NewsletterEditorPage() {
  const navigate = useNavigate();
  const { id, schoolSlug } = useParams<{ id: string; schoolSlug: string }>();
  const isNew = id === 'new';

  // Form state
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [visibility, setVisibility] = useState<ContentVisibility>('DRAFT');
  const [sendToAll, setSendToAll] = useState(true);
  const [targetClasses, setTargetClasses] = useState<string[]>([]);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetInstruments, setTargetInstruments] = useState<string[]>([]);

  // Dialog state
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Queries and mutations
  const { data: newsletter, isLoading } = useNewsletter(id || '');
  const createMutation = useCreateNewsletter();
  const updateMutation = useUpdateNewsletter();
  const addSectionMutation = useAddSection();
  const updateSectionMutation = useUpdateSection();
  const deleteSectionMutation = useDeleteSection();
  // reorderSectionsMutation available for drag-and-drop reordering (future enhancement)
  useReorderSections();
  const publishMutation = usePublishNewsletter();
  const sendMutation = useSendNewsletter();
  const scheduleMutation = useScheduleNewsletter();

  // Load newsletter data
  useEffect(() => {
    if (newsletter) {
      setTitle(newsletter.title);
      setExcerpt(newsletter.excerpt || '');
      setVisibility(newsletter.visibility);
      setSendToAll(newsletter.sendToAll);
      setTargetClasses(newsletter.targetClasses);
      setTargetAgeGroups(newsletter.targetAgeGroups);
      setTargetInstruments(newsletter.targetInstruments);
    }
  }, [newsletter]);

  const handleSave = async () => {
    try {
      if (isNew) {
        const data: CreateNewsletterData = {
          title,
          excerpt: excerpt || undefined,
          visibility,
          sendToAll,
          targetClasses,
          targetAgeGroups,
          targetInstruments,
        };
        const created = await createMutation.mutateAsync(data);
        navigate(`/${schoolSlug}/admin/newsletters/${created.id}`, { replace: true });
      } else if (id) {
        const data: UpdateNewsletterData = {
          title,
          excerpt: excerpt || null,
          visibility,
          sendToAll,
          targetClasses,
          targetAgeGroups,
          targetInstruments,
        };
        await updateMutation.mutateAsync({ newsletterId: id, data });
      }
    } catch (err) {
      console.error('Failed to save newsletter:', err);
    }
  };

  const handleAddSection = async (data: CreateSectionData) => {
    if (!id || isNew) return;
    try {
      await addSectionMutation.mutateAsync({ newsletterId: id, data });
      setAddSectionOpen(false);
    } catch (err) {
      console.error('Failed to add section:', err);
    }
  };

  const handleUpdateSection = async (sectionId: string, data: UpdateSectionData) => {
    if (!id) return;
    try {
      await updateSectionMutation.mutateAsync({ newsletterId: id, sectionId, data });
    } catch (err) {
      console.error('Failed to update section:', err);
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!id) return;
    try {
      await deleteSectionMutation.mutateAsync({ newsletterId: id, sectionId });
    } catch (err) {
      console.error('Failed to delete section:', err);
    }
  };

  const handlePublish = async () => {
    if (!id) return;
    try {
      await publishMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to publish newsletter:', err);
    }
  };

  const handleSend = async () => {
    if (!id) return;
    try {
      await sendMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to send newsletter:', err);
    }
  };

  const handleSchedule = async (scheduledFor: string) => {
    if (!id) return;
    try {
      await scheduleMutation.mutateAsync({ newsletterId: id, data: { scheduledFor } });
      setScheduleOpen(false);
    } catch (err) {
      console.error('Failed to schedule newsletter:', err);
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const sections = newsletter?.sections || [];
  const canPublish = newsletter?.status === 'DRAFT' && sections.length > 0;
  const canSend = newsletter?.visibility === 'PORTAL' || newsletter?.visibility === 'PUBLIC';
  const canSchedule = newsletter?.status === 'DRAFT';

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Create Newsletter' : 'Edit Newsletter'}
        subtitle={newsletter?.title || 'New newsletter'}
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/newsletters`}>
              Back
            </Button>
            {!isNew && (
              <>
                <Button startIcon={<PreviewIcon />} component={Link} to={`/${schoolSlug}/admin/newsletters/${id}/preview`}>
                  Preview
                </Button>
                {canSchedule && (
                  <Button
                    startIcon={<ScheduleIcon />}
                    onClick={() => setScheduleOpen(true)}
                    disabled={scheduleMutation.isPending}
                  >
                    Schedule
                  </Button>
                )}
                {canPublish && (
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={handlePublish}
                    disabled={publishMutation.isPending}
                  >
                    Publish
                  </Button>
                )}
                {canSend && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={sendMutation.isPending}
                  >
                    Send Now
                  </Button>
                )}
              </>
            )}
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !title.trim()}
            >
              Save
            </Button>
          </Stack>
        }
      />

      {/* Error alerts */}
      {createMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create newsletter
        </Alert>
      )}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update newsletter
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Main content */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Newsletter Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Brief description for preview"
              />
            </Stack>
          </Paper>

          {/* Sections */}
          {!isNew && (
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Sections</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setAddSectionOpen(true)}
                  size="small"
                >
                  Add Section
                </Button>
              </Stack>

              {sections.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No sections yet. Click "Add Section" to get started.
                </Typography>
              ) : (
                sections
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((section) => (
                    <SectionEditor
                      key={section.id}
                      section={section}
                      onUpdate={(data) => handleUpdateSection(section.id, data)}
                      onDelete={() => handleDeleteSection(section.id)}
                      isUpdating={updateSectionMutation.isPending}
                    />
                  ))
              )}
            </Paper>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={visibility}
                  label="Visibility"
                  onChange={(e) => setVisibility(e.target.value as ContentVisibility)}
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="PORTAL">Portal Only</MenuItem>
                  <MenuItem value="PUBLIC">Public Blog</MenuItem>
                </Select>
              </FormControl>

              <Divider />

              <Typography variant="subtitle2">Distribution</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendToAll}
                    onChange={(e) => setSendToAll(e.target.checked)}
                  />
                }
                label="Send to all parents"
              />

              {!sendToAll && (
                <>
                  <Autocomplete
                    multiple
                    options={['MINI', 'MASTER', 'MEZZO', 'MOLTO', 'MAESTRO', 'VOICE']}
                    value={targetAgeGroups}
                    onChange={(_, value) => setTargetAgeGroups(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Target Age Groups" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />
                  <Autocomplete
                    multiple
                    options={['Piano', 'Guitar', 'Drums', 'Singing', 'Bass', 'Preschool']}
                    value={targetInstruments}
                    onChange={(_, value) => setTargetInstruments(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Target Instruments" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />
                </>
              )}
            </Stack>
          </Paper>

          {/* Status info */}
          {newsletter && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip label={newsletter.status} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Recipients
                  </Typography>
                  <Typography variant="body2">{newsletter.totalRecipients}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Sent
                  </Typography>
                  <Typography variant="body2">{newsletter.sentCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Opens
                  </Typography>
                  <Typography variant="body2">{newsletter.openCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Clicks
                  </Typography>
                  <Typography variant="body2">{newsletter.clickCount}</Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>

      {/* Dialogs */}
      <AddSectionDialog
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        onAdd={handleAddSection}
        isAdding={addSectionMutation.isPending}
      />

      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onSchedule={handleSchedule}
        isScheduling={scheduleMutation.isPending}
      />
    </Box>
  );
}
